import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { WikiArticle } from '@/context/WikiContext';

interface QuizOption {
  label: string;
  icon: string;
  desc: string;
  scores: Record<string, number>;
}

interface QuizQuestion {
  id: string;
  question: string;
  icon: string;
  options: QuizOption[];
}

interface Props {
  articles: WikiArticle[];
  onSelectArticle: (id: string) => void;
  onClose: () => void;
}

const QUESTIONS: QuizQuestion[] = [
  {
    id: 'usage',
    question: 'How do you primarily use your boat?',
    icon: 'ri-ship-line',
    options: [
      { label: 'Day trips', icon: 'ri-sun-line', desc: 'Short weekend outings', scores: { lifepo4: 1, agm: 3, gel: 2 } },
      { label: 'Extended cruising / offshore', icon: 'ri-compass-3-line', desc: 'Multi-day open water passages', scores: { lifepo4: 3, agm: 1, gel: 2 } },
      { label: 'Liveaboard', icon: 'ri-home-heart-line', desc: 'Living aboard full-time', scores: { lifepo4: 3, agm: 1, gel: 3 } },
      { label: 'Fishing / trolling', icon: 'ri-anchor-line', desc: 'Electric motors and heavy gear', scores: { lifepo4: 3, agm: 2, gel: 1 } },
    ],
  },
  {
    id: 'electronics',
    question: 'How electronics-heavy is your vessel?',
    icon: 'ri-radar-line',
    options: [
      { label: 'Minimal', icon: 'ri-dashboard-line', desc: 'Basic navigation only', scores: { lifepo4: 1, agm: 3, gel: 1 } },
      { label: 'Moderate', icon: 'ri-cpu-line', desc: 'GPS, VHF, fishfinder', scores: { lifepo4: 2, agm: 2, gel: 2 } },
      { label: 'Heavy electronics', icon: 'ri-router-line', desc: 'Radar, autopilot, chartplotter, audio', scores: { lifepo4: 2, agm: 1, gel: 3 } },
    ],
  },
  {
    id: 'budget',
    question: 'What is your budget priority?',
    icon: 'ri-money-dollar-circle-line',
    options: [
      { label: 'Lowest upfront cost', icon: 'ri-price-tag-3-line', desc: 'Keep initial spend low', scores: { lifepo4: 0, agm: 3, gel: 2 } },
      { label: 'Long-term savings', icon: 'ri-line-chart-line', desc: 'Fewer replacements, lower total cost', scores: { lifepo4: 3, agm: 1, gel: 2 } },
      { label: 'Best performance', icon: 'ri-trophy-line', desc: 'Price is secondary', scores: { lifepo4: 3, agm: 1, gel: 2 } },
    ],
  },
  {
    id: 'weight',
    question: 'How important is weight to you?',
    icon: 'ri-scales-3-line',
    options: [
      { label: 'Critical', icon: 'ri-speed-up-line', desc: 'Performance and fuel efficiency matter', scores: { lifepo4: 3, agm: 1, gel: 1 } },
      { label: 'Somewhat important', icon: 'ri-scales-line', desc: 'Lighter is nice but not essential', scores: { lifepo4: 2, agm: 2, gel: 2 } },
      { label: 'Not a concern', icon: 'ri-anchor-line', desc: 'Weight is not an issue', scores: { lifepo4: 1, agm: 3, gel: 2 } },
    ],
  },
  {
    id: 'climate',
    question: 'What climate do you typically boat in?',
    icon: 'ri-temp-hot-line',
    options: [
      { label: 'Cold climate', icon: 'ri-snowy-line', desc: 'Northern waters, low temperatures', scores: { lifepo4: 1, agm: 3, gel: 1 } },
      { label: 'Temperate', icon: 'ri-cloud-line', desc: 'Four-season moderate weather', scores: { lifepo4: 3, agm: 3, gel: 2 } },
      { label: 'Hot / tropical', icon: 'ri-sun-fill', desc: 'Mediterranean, tropical regions', scores: { lifepo4: 2, agm: 2, gel: 3 } },
    ],
  },
];

function calcScores(answers: number[], articles: WikiArticle[]): [WikiArticle, number][] {
  const totals: Record<string, number> = {};
  articles.forEach((a) => { totals[a.id] = 0; });

  answers.forEach((optIdx, qIdx) => {
    const q = QUESTIONS[qIdx];
    if (!q) return;
    const opt = q.options[optIdx];
    if (!opt) return;
    Object.entries(opt.scores).forEach(([id, score]) => {
      if (totals[id] !== undefined) totals[id] += score;
    });
  });

  return articles
    .map((a): [WikiArticle, number] => [a, totals[a.id] ?? 0])
    .sort((a, b) => b[1] - a[1]);
}

// ── Intro Screen ──────────────────────────────────────────────────────────────
function IntroScreen({ onStart, onClose, light }: { onStart: () => void; onClose: () => void; light: boolean }) {
  const bg = light ? 'bg-white' : 'bg-[#1C2B3A]';
  const border = light ? 'border-gray-100' : 'border-white/5';
  const textPrimary = light ? 'text-gray-900' : 'text-white';
  const textSecondary = light ? 'text-gray-500' : 'text-white/50';

  return (
    <div className={`rounded-2xl border ${border} ${bg} overflow-hidden`}>
      <div className="relative p-8 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 50% 0%, var(--accent), transparent 70%)' }} />
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors z-10 ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/10 text-white/30'}`}
        >
          <i className="ri-close-line text-sm"></i>
        </button>
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 text-white text-2xl relative z-10"
          style={{ background: 'var(--accent)' }}
        >
          <i className="ri-questionnaire-line"></i>
        </div>
        <h2 className={`text-2xl font-black mb-3 relative z-10 ${textPrimary}`}>Which Battery Is Right for Me?</h2>
        <p className={`text-sm leading-relaxed max-w-xs mx-auto mb-6 relative z-10 ${textSecondary}`}>
          Answer {QUESTIONS.length} quick questions and we&apos;ll find the best battery technology for your needs.
        </p>
        <div className="flex flex-col gap-2 mb-8 relative z-10">
          {[
            { icon: 'ri-time-line', text: 'Takes about 1 minute' },
            { icon: 'ri-shield-check-line', text: 'Personalized recommendation' },
            { icon: 'ri-bar-chart-line', text: 'Compatibility score included' },
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-2 text-xs justify-center ${textSecondary}`}>
              <i className={item.icon} style={{ color: 'var(--accent)' }}></i>
              {item.text}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onStart}
          className="relative z-10 w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer whitespace-nowrap"
          style={{ background: 'var(--accent)' }}
        >
          Get Started <i className="ri-arrow-right-line ml-1"></i>
        </button>
      </div>
    </div>
  );
}

// ── Question Screen ───────────────────────────────────────────────────────────
function QuestionScreen({
  question, qIndex, total, selected, fading,
  onSelect, onNext, onClose, light,
}: {
  question: QuizQuestion;
  qIndex: number;
  total: number;
  selected: number | null;
  fading: boolean;
  onSelect: (i: number) => void;
  onNext: () => void;
  onClose: () => void;
  light: boolean;
}) {
  const bg = light ? 'bg-white' : 'bg-[#1C2B3A]';
  const border = light ? 'border-gray-100' : 'border-white/5';
  const textPrimary = light ? 'text-gray-900' : 'text-white';
  const textSecondary = light ? 'text-gray-500' : 'text-white/50';
  const progress = (qIndex / total) * 100;

  return (
    <div className={`rounded-2xl border ${border} ${bg} overflow-hidden`}>
      {/* Progress bar */}
      <div className={`h-1 w-full ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${progress}%`, background: 'var(--accent)' }}
        />
      </div>

      <div className={`transition-opacity duration-200 ${fading ? 'opacity-0' : 'opacity-100'}`}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <span className={`text-xs font-bold ${textSecondary}`}>Question {qIndex + 1} of {total}</span>
            <button
              type="button"
              onClick={onClose}
              className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/10 text-white/30'}`}
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0 mt-0.5" style={{ background: 'var(--accent)' }}>
              <i className={question.icon}></i>
            </div>
            <h3 className={`text-base font-bold leading-snug ${textPrimary}`}>{question.question}</h3>
          </div>
        </div>

        {/* Options */}
        <div className="px-6 pb-4 space-y-2">
          {question.options.map((opt, i) => {
            const isSelected = selected === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onSelect(i)}
                className={`w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all duration-150 cursor-pointer ${
                  isSelected
                    ? 'border-transparent'
                    : light
                    ? 'border-gray-100 bg-gray-50 hover:bg-gray-100'
                    : 'border-white/5 bg-white/3 hover:bg-white/8'
                }`}
                style={isSelected ? { background: 'var(--accent)', borderColor: 'var(--accent)' } : {}}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-white/20' : light ? 'bg-white' : 'bg-white/5'}`}
                >
                  <i
                    className={`${opt.icon} text-sm`}
                    style={{ color: isSelected ? 'white' : 'var(--accent)' }}
                  ></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${isSelected ? 'text-white' : textPrimary}`}>{opt.label}</p>
                  <p className={`text-xs mt-0.5 ${isSelected ? 'text-white/70' : textSecondary}`}>{opt.desc}</p>
                </div>
                {isSelected && (
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Next */}
        <div className={`px-6 py-4 border-t ${border}`}>
          <button
            type="button"
            onClick={onNext}
            disabled={selected === null}
            className={`w-full py-3 rounded-xl text-sm font-bold text-white cursor-pointer whitespace-nowrap transition-opacity ${selected === null ? 'opacity-30 cursor-not-allowed' : 'opacity-100'}`}
            style={{ background: 'var(--accent)' }}
          >
            {qIndex < total - 1
              ? <span>Next <i className="ri-arrow-right-line ml-1"></i></span>
              : <span>See My Result <i className="ri-magic-line ml-1"></i></span>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Result Screen ─────────────────────────────────────────────────────────────
function ResultScreen({
  ranked, onViewArticle, onRetake, onClose, light,
}: {
  ranked: [WikiArticle, number][];
  onViewArticle: (id: string) => void;
  onRetake: () => void;
  onClose: () => void;
  light: boolean;
}) {
  const bg = light ? 'bg-white' : 'bg-[#1C2B3A]';
  const border = light ? 'border-gray-100' : 'border-white/5';
  const textPrimary = light ? 'text-gray-900' : 'text-white';
  const textSecondary = light ? 'text-gray-500' : 'text-white/50';

  const winner = ranked[0]?.[0];
  const maxScore = ranked[0]?.[1] ?? 1;
  const second = ranked[1]?.[0];

  if (!winner) return null;

  return (
    <div className={`rounded-2xl border ${border} ${bg} overflow-hidden`}>
      {/* Header */}
      <div className="relative p-8 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 50% 0%, ${winner.badgeColor}, transparent 70%)` }} />
        <button
          type="button"
          onClick={onClose}
          className={`absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors z-10 ${light ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/10 text-white/30'}`}
        >
          <i className="ri-close-line text-sm"></i>
        </button>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white text-2xl relative z-10" style={{ background: winner.badgeColor }}>
          <i className={winner.icon}></i>
        </div>
        <p className={`text-xs font-bold uppercase tracking-widest mb-2 relative z-10 ${textSecondary}`}>Best match for you</p>
        <h2 className={`text-3xl font-black mb-1 relative z-10 ${textPrimary}`}>{winner.title}</h2>
        <p className={`text-sm relative z-10 ${textSecondary}`}>{winner.subtitle}</p>
      </div>

      {/* Score bars */}
      <div className={`px-8 py-6 border-t ${border}`}>
        <p className={`text-xs font-bold uppercase tracking-widest mb-4 ${textSecondary}`}>Compatibility Score</p>
        <div className="space-y-3">
          {ranked.map(([art, score], i) => {
            const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
            return (
              <div key={art.id}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {i === 0 && <i className="ri-trophy-fill text-yellow-400 text-sm"></i>}
                    <span className={`text-sm font-semibold ${textPrimary}`}>{art.title}</span>
                  </div>
                  <span className="text-xs font-bold" style={{ color: art.badgeColor }}>{pct}%</span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: art.badgeColor }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Winner summary */}
      <div className={`px-8 py-5 border-t ${border}`}>
        <p className={`text-sm leading-relaxed ${textSecondary}`}>{winner.summary}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {winner.pros.slice(0, 3).map((p, i) => (
            <span key={i} className="text-xs px-3 py-1 rounded-full font-medium" style={{ background: `${winner.badgeColor}18`, color: winner.badgeColor }}>
              <i className="ri-check-line mr-1"></i>{p.text}
            </span>
          ))}
        </div>
      </div>

      {/* Alternative */}
      {second && (
        <div className={`px-8 py-4 border-t ${border}`}>
          <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${textSecondary}`}>Alternative option</p>
          <div className={`flex items-center gap-3 p-3 rounded-xl ${light ? 'bg-gray-50' : 'bg-white/5'}`}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: second.badgeColor }}>
              <i className={second.icon}></i>
            </div>
            <div>
              <p className={`text-sm font-bold ${textPrimary}`}>{second.title}</p>
              <p className={`text-xs ${textSecondary}`}>{second.subtitle}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className={`px-8 py-5 border-t ${border} flex flex-col sm:flex-row gap-3`}>
        <button
          type="button"
          onClick={() => onViewArticle(winner.id)}
          className="flex-1 py-3 rounded-xl text-sm font-bold text-white cursor-pointer whitespace-nowrap"
          style={{ background: winner.badgeColor }}
        >
          <i className="ri-book-open-line mr-2"></i>Read Full Guide
        </button>
        <button
          type="button"
          onClick={onRetake}
          className={`flex-1 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap border ${border} ${textSecondary} ${light ? 'hover:bg-gray-50' : 'hover:bg-white/5'}`}
        >
          <i className="ri-refresh-line mr-2"></i>Retake Quiz
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function BatteryQuiz({ articles, onSelectArticle, onClose }: Props) {
  const { theme } = useTheme();
  const light = theme === 'light';

  const [screen, setScreen] = useState<'intro' | 'quiz' | 'result'>('intro');
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [fading, setFading] = useState(false);

  const handleStart = () => {
    setScreen('quiz');
    setQIndex(0);
    setAnswers([]);
    setSelected(null);
  };

  const handleSelect = (i: number) => {
    setSelected(i);
  };

  const handleNext = () => {
    if (selected === null) return;
    const newAnswers = [...answers, selected];
    setAnswers(newAnswers);

    setFading(true);
    setTimeout(() => {
      if (qIndex < QUESTIONS.length - 1) {
        setQIndex(qIndex + 1);
        setSelected(null);
      } else {
        setScreen('result');
      }
      setFading(false);
    }, 200);
  };

  const handleRetake = () => {
    setScreen('intro');
    setQIndex(0);
    setAnswers([]);
    setSelected(null);
    setFading(false);
  };

  const handleViewArticle = (id: string) => {
    onSelectArticle(id);
    onClose();
  };

  if (screen === 'intro') {
    return <IntroScreen onStart={handleStart} onClose={onClose} light={light} />;
  }

  if (screen === 'result') {
    const ranked = calcScores(answers, articles);
    return (
      <ResultScreen
        ranked={ranked}
        onViewArticle={handleViewArticle}
        onRetake={handleRetake}
        onClose={onClose}
        light={light}
      />
    );
  }

  return (
    <QuestionScreen
      question={QUESTIONS[qIndex]}
      qIndex={qIndex}
      total={QUESTIONS.length}
      selected={selected}
      fading={fading}
      onSelect={handleSelect}
      onNext={handleNext}
      onClose={onClose}
      light={light}
    />
  );
}
